// src/components/dashboard/PerformanceWellbeingMap.jsx
//
// Dual-dimension Performance x Wellbeing map.
// TONE: wellbeing is shown as ONE aggregate score + tier — never named
// clinical screeners. At-risk states use calm rose/amber, never alarm red.

import React from 'react';
import { Activity, HeartHandshake, Sparkles, Map, ChevronRight, Eye } from 'lucide-react';

// Tier -> calm, supportive color treatments (no aggressive red)
const TIER_STYLES = {
    'Healthy':  { text: 'text-emerald-600', bg: 'bg-emerald-50', border: 'border-emerald-100', ring: '#10b981' },
    'Mild':     { text: 'text-teal-600',    bg: 'bg-teal-50',    border: 'border-teal-100',    ring: '#14b8a6' },
    'Elevated': { text: 'text-amber-600',   bg: 'bg-amber-50',   border: 'border-amber-100',   ring: '#f59e0b' },
    'At Risk':  { text: 'text-rose-500',    bg: 'bg-rose-50',    border: 'border-rose-100',    ring: '#fb7185' },
    'Unknown':  { text: 'text-slate-400',   bg: 'bg-slate-50',   border: 'border-slate-100',   ring: '#cbd5e1' }
};

const QUADRANT_ZONES = [
    // grid order: top-left, top-right, bottom-left, bottom-right
    { id: 'building',      label: 'Building',      corner: 'rounded-tl-2xl' },
    { id: 'thriving',      label: 'Thriving',      corner: 'rounded-tr-2xl' },
    { id: 'needs_support', label: 'Needs Support', corner: 'rounded-bl-2xl' },
    { id: 'running_hot',   label: 'Running Hot',   corner: 'rounded-br-2xl' }
];

const ScoreRing = ({ value, color, label, sublabel, subColor }) => {
    const r = 34;
    const c = 2 * Math.PI * r;
    const offset = c - ((value || 0) / 100) * c;
    return (
        <div className="flex flex-col items-center text-center">
            <div className="relative w-24 h-24 flex items-center justify-center">
                <svg className="w-full h-full transform -rotate-90" viewBox="0 0 88 88">
                    <circle cx="44" cy="44" r={r} stroke="#f1f5f9" strokeWidth="8" fill="transparent" />
                    <circle
                        cx="44" cy="44" r={r}
                        stroke={color} strokeWidth="8" fill="transparent"
                        strokeDasharray={c} strokeDashoffset={offset}
                        strokeLinecap="round" className="transition-all duration-1000 ease-out"
                    />
                </svg>
                <span className="absolute text-2xl font-black text-slate-800">{value}</span>
            </div>
            <p className="mt-2 text-[10px] font-black text-slate-400 uppercase tracking-widest">{label}</p>
            {sublabel && (
                <span className={`mt-1 text-[10px] font-black uppercase tracking-tight px-2.5 py-0.5 rounded-full ${subColor}`}>
                    {sublabel}
                </span>
            )}
        </div>
    );
};

// tierLabel / zoneLabels: presentation overrides (data/youthCopy.js) for a
// member under 18. The tier and quadrant ids underneath are unchanged.
const PerformanceWellbeingMap = ({ perfScore, wellbeingScore, wellbeingTier, quadrant, divergence, safetyFlag, hasData, onNavigate, onGetHelp, tierLabel = null, zoneLabels = null }) => {

    // --- NO BASELINE YET: invite, don't render zeros ---
    if (!hasData) {
        return (
            <div className="bg-white p-8 rounded-3xl border border-slate-200 shadow-sm flex flex-col items-center text-center">
                <div className="w-14 h-14 bg-brand-light text-brand-primary rounded-2xl flex items-center justify-center mb-4">
                    <Map size={28} />
                </div>
                <h3 className="text-lg font-black text-slate-900 mb-1">Your Performance &amp; Wellbeing Map</h3>
                <p className="text-sm text-slate-500 max-w-md mb-5 font-medium">
                    Complete your baseline assessment to see where you sit on the Performance x Wellbeing map and what to focus on next.
                </p>
                <button
                    onClick={() => onNavigate('assessment')}
                    className="px-6 py-3 bg-brand-primary text-white rounded-full font-bold hover:bg-brand-dark transition-all shadow-md flex items-center gap-2"
                >
                    Start Baseline <ChevronRight size={16} />
                </button>
            </div>
        );
    }

    const tierStyle = TIER_STYLES[wellbeingTier] || TIER_STYLES.Unknown;
    const hasWellbeing = wellbeingScore !== null && quadrant?.id !== 'unknown';
    const perfColor = (perfScore || 0) >= 60 ? '#10b981' : '#f59e0b';

    // Dot position: X = performance (left->right), Y = wellbeing (bottom->top)
    const dotX = Math.min(96, Math.max(4, perfScore || 0));
    const dotY = Math.min(96, Math.max(4, 100 - (wellbeingScore || 0)));

    return (
        <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 shadow-sm relative overflow-hidden">
            <div className="flex items-center gap-2 mb-6">
                <Map size={20} className="text-brand-primary" />
                <h3 className="text-lg font-black text-slate-800">Performance &amp; Wellbeing Map</h3>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">

                {/* LEFT: the two peer scores */}
                <div className="lg:col-span-2 flex flex-col gap-6">
                    <div className="grid grid-cols-2 gap-4">
                        {/* Performance */}
                        <div className="bg-slate-50 rounded-2xl border border-slate-100 p-4 flex flex-col items-center">
                            <div className="flex items-center gap-1.5 text-slate-400 mb-2">
                                <Activity size={14} />
                                <span className="text-[10px] font-black uppercase tracking-widest">Output</span>
                            </div>
                            <ScoreRing value={perfScore} color={perfColor} label="Performance" />
                        </div>

                        {/* Wellbeing */}
                        {hasWellbeing ? (
                            <div className={`${tierStyle.bg} rounded-2xl border ${tierStyle.border} p-4 flex flex-col items-center`}>
                                <div className={`flex items-center gap-1.5 ${tierStyle.text} opacity-70 mb-2`}>
                                    <HeartHandshake size={14} />
                                    <span className="text-[10px] font-black uppercase tracking-widest">Inner State</span>
                                </div>
                                <ScoreRing
                                    value={wellbeingScore}
                                    color={tierStyle.ring}
                                    label="Wellbeing"
                                    sublabel={tierLabel || wellbeingTier}
                                    subColor={`${tierStyle.bg} ${tierStyle.text} border ${tierStyle.border}`}
                                />
                            </div>
                        ) : (
                            <div className="bg-slate-50 rounded-2xl border border-dashed border-slate-200 p-4 flex flex-col items-center justify-center text-center">
                                <div className="w-10 h-10 bg-white text-brand-primary rounded-xl flex items-center justify-center mb-2 shadow-sm">
                                    <HeartHandshake size={20} />
                                </div>
                                <p className="text-xs text-slate-500 font-medium leading-snug mb-3">
                                    Complete your full check-in to unlock your Wellbeing map.
                                </p>
                                <button
                                    onClick={() => onNavigate('assessment')}
                                    className="text-xs font-bold text-brand-primary bg-brand-light hover:bg-blue-100 px-3 py-1.5 rounded-full transition-colors flex items-center gap-1"
                                >
                                    Complete Check-in <ChevronRight size={12} />
                                </button>
                            </div>
                        )}
                    </div>

                    {/* Current quadrant summary */}
                    {hasWellbeing && (
                        <div className="bg-slate-50 rounded-2xl border border-slate-100 p-4">
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Your current zone</p>
                            <p className="text-xl font-black text-slate-900">{quadrant.label}</p>
                            <p className="text-xs text-slate-500 font-medium leading-relaxed mt-1">{quadrant.description}</p>
                        </div>
                    )}
                </div>

                {/* RIGHT: the 2x2 quadrant map */}
                <div className="lg:col-span-3">
                    {hasWellbeing ? (
                        <div className="flex gap-3">
                            {/* Y axis label */}
                            <div className="flex flex-col justify-between items-center py-2 shrink-0">
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest" style={{ writingMode: 'vertical-rl', transform: 'rotate(180deg)' }}>
                                    Wellbeing → Healthy
                                </span>
                            </div>

                            <div className="flex-1">
                                <div className="relative w-full aspect-square max-h-[340px]">
                                    {/* zone grid */}
                                    <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-1">
                                        {QUADRANT_ZONES.map(zone => {
                                            const isActive = quadrant.id === zone.id;
                                            return (
                                                <div
                                                    key={zone.id}
                                                    className={`${zone.corner} flex items-start justify-start p-3 border transition-colors ${
                                                        isActive
                                                            ? 'bg-brand-light border-brand-primary/40'
                                                            : 'bg-slate-50 border-slate-100'
                                                    }`}
                                                >
                                                    <span className={`text-[10px] font-black uppercase tracking-widest ${
                                                        isActive ? 'text-brand-primary' : 'text-slate-300'
                                                    }`}>
                                                        {(zoneLabels && zoneLabels[zone.id]) || zone.label}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* user dot */}
                                    <div
                                        className="absolute z-10 -translate-x-1/2 -translate-y-1/2 transition-all duration-1000 ease-out"
                                        style={{ left: `${dotX}%`, top: `${dotY}%` }}
                                    >
                                        <span className="absolute inset-0 w-5 h-5 -m-1 rounded-full bg-brand-primary/30 animate-ping" />
                                        <span className="relative block w-3 h-3 rounded-full bg-brand-primary ring-4 ring-white shadow-md" />
                                    </div>
                                </div>

                                {/* X axis label */}
                                <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest text-right mt-2">
                                    Performance → High
                                </p>
                            </div>
                        </div>
                    ) : (
                        <div className="h-full min-h-[240px] rounded-2xl border border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-center p-6">
                            <Sparkles size={24} className="text-slate-300 mb-2" />
                            <p className="text-sm font-bold text-slate-500">Your map is waiting</p>
                            <p className="text-xs text-slate-400 font-medium max-w-xs mt-1">
                                Once your full check-in is complete, you'll see exactly where you sit across Performance and Wellbeing.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* DIVERGENCE INSIGHT — supportive, "worth noticing" framing */}
            {hasWellbeing && divergence?.flag && divergence.pattern && (
                <div className="mt-6 bg-amber-50 border border-amber-100 rounded-2xl p-4 flex items-start gap-3">
                    <div className="p-2 bg-white text-amber-500 rounded-xl shadow-sm shrink-0">
                        <Eye size={18} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest mb-0.5">Worth noticing</p>
                        <p className="text-sm text-slate-600 font-medium leading-relaxed">{divergence.pattern}.</p>
                    </div>
                </div>
            )}

            {/* SUPPORTIVE SAFETY CARD — gentle, never alarming or diagnostic */}
            {safetyFlag && (
                <div className="mt-4 bg-rose-50 border border-rose-100 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                    <div className="p-2.5 bg-white text-rose-400 rounded-xl shadow-sm shrink-0">
                        <HeartHandshake size={20} />
                    </div>
                    <div className="flex-1">
                        <p className="text-sm font-bold text-slate-800">It can help to talk to someone.</p>
                        <p className="text-xs text-slate-500 font-medium leading-relaxed mt-0.5">
                            Your recent check-in suggests you may be carrying a lot right now. Support and resources are here whenever you're ready — no pressure.
                        </p>
                    </div>
                    <button
                        onClick={() => onGetHelp ? onGetHelp() : onNavigate('mental-health')}
                        className="shrink-0 text-xs font-bold text-rose-500 bg-white border border-rose-100 hover:bg-rose-100/60 px-4 py-2.5 rounded-xl transition-colors flex items-center gap-1.5"
                    >
                        {onGetHelp ? 'Talk to Someone' : 'Explore Support'} <ChevronRight size={14} />
                    </button>
                </div>
            )}
        </div>
    );
};

export default PerformanceWellbeingMap;
